import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import API from "helpers/api";
import { PRODUCT_UPDATE_NOTIFICATION_REFRESH_EVENT } from "helpers/constants";
import { defaultPageSize } from "helpers/utils";
import useInfiniteScrollWithoutCount from "hooks/useInfiniteScrollWithoutCount";
import { BANNER_HEIGHT_VAR } from "components/Layouts/MaintenanceBanner/MaintenanceBanner";

const LIST_SCROLL_ID = "release-notes-list-scroll";

const COLORS = {
	text: "#393939",
	muted: "#545353",
	divider: "#C5C5C8",
	blue: "#307AD7",
	hover: "#EEEEEE",
	white: "#FFFFFF",
	heroBg: "#D2D2D9",
};

function broadcastRefresh() {
	window.dispatchEvent(new CustomEvent(PRODUCT_UPDATE_NOTIFICATION_REFRESH_EVENT));
}

function formatDate(value) {
	const date = value ? new Date(value) : null;
	if (!date || Number.isNaN(date.getTime())) return "";
	return new Intl.DateTimeFormat(navigator.language || "en", {
		day: "numeric",
		month: "short",
		year: "numeric",
	}).format(date);
}

function audiencesFor(update) {
	if (update.audience === "web") return ["Web"];
	if (update.audience === "mobile") return ["Mobile"];
	return ["Web", "Mobile"];
}

const AUDIENCE_CHIP = {
	Web: { bg: "rgba(48,122,215,0.12)", fg: "#307AD7" },
	Mobile: { bg: "rgba(35,187,121,0.16)", fg: "#1A8A5A" },
};

const chipSx = (name) => ({
	display: "inline-flex",
	alignItems: "center",
	px: 0.75,
	py: "1px",
	borderRadius: "2px",
	font: "700 10px 'Roboto Condensed', sans-serif",
	letterSpacing: 0.4,
	textTransform: "uppercase",
	backgroundColor: AUDIENCE_CHIP[name].bg,
	color: AUDIENCE_CHIP[name].fg,
});

function ReleaseNotesPage() {
	const [items, setItems] = useState([]);
	const [selectedId, setSelectedId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [scrollEvent, setScrollEvent] = useState(window);
	const listRef = useRef(null);
	const pageSize = defaultPageSize();

	const fetchPage = async (p) => {
		try {
			const res = await API.get(
				`/api/product-updates?page=${p}&pageSize=${pageSize}`
			);
			const list = res?.data?.items || [];
			setPage(p);
			setItems((prev) => [...prev, ...list]);
			return list;
		} catch {
			return [];
		}
	};

	const { loading: loadingMore, hasMoreRef } = useInfiniteScrollWithoutCount(
		async (currentPage) => await fetchPage(currentPage + 1),
		page,
		scrollEvent,
		LIST_SCROLL_ID
	);

	useEffect(() => {
		let cancelled = false;
		API.get(`/api/product-updates?page=1&pageSize=${pageSize}`)
			.then((res) => {
				if (cancelled) return;
				const list = res?.data?.items || [];
				hasMoreRef.current = true;
				setItems(list);
				setPage(1);
				if (list.length > 0) setSelectedId(list[0].id);
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [pageSize, hasMoreRef]);

	// Page against the list panel's own scroll, not the window.
	useEffect(() => {
		if (!loading && listRef.current) setScrollEvent(listRef.current);
	}, [loading]);

	const selected = useMemo(
		() => items.find((x) => x.id === selectedId) || null,
		[items, selectedId]
	);

	const markRead = (id) => {
		setItems((prev) =>
			prev.map((x) => (x.id === id ? { ...x, isRead: true } : x))
		);
		API.post(`/api/product-updates/${id}/mark-read`)
			.then(broadcastRefresh)
			.catch(() => {});
	};

	const markUnread = (id) => {
		setItems((prev) =>
			prev.map((x) => (x.id === id ? { ...x, isRead: false } : x))
		);
		API.post(`/api/product-updates/${id}/mark-unread`)
			.then(broadcastRefresh)
			.catch(() => {});
	};

	const toggleRead = (update) =>
		update.isRead ? markUnread(update.id) : markRead(update.id);

	const handleSelect = (update) => {
		setSelectedId(update.id);
		if (!update.isRead) markRead(update.id);
	};

	const handleMarkAllRead = () => {
		setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
		API.post("/api/product-updates/mark-all-read")
			.then(broadcastRefresh)
			.catch(() => {});
	};

	const hasUnread = items.some((x) => !x.isRead);

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				// Fill the space: viewport minus the (dynamic) maintenance banner and the
				// <main> padding (48px), so only the inner list/detail panels scroll.
				height: `calc(100vh - var(${BANNER_HEIGHT_VAR}, 0px) - 48px)`,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
					flexShrink: 0,
				}}
			>
				<Box
					sx={{
						font: "700 24px 'Roboto Condensed', sans-serif",
						color: COLORS.text,
					}}
				>
					Release notes
				</Box>
				{hasUnread ? (
					<Box
						component="button"
						type="button"
						onClick={handleMarkAllRead}
						sx={{
							background: "none",
							border: "none",
							cursor: "pointer",
							color: COLORS.blue,
							font: "700 11px 'Roboto Condensed', sans-serif",
							textTransform: "uppercase",
							letterSpacing: 0.3,
						}}
					>
						Mark all read
					</Box>
				) : null}
			</Box>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					// List sits on the right (under "Mark all read"); detail fills the left.
					flexDirection: "row-reverse",
					border: `1px solid ${COLORS.divider}`,
					backgroundColor: COLORS.white,
				}}
			>
				<Box
					sx={{
						width: 380,
						borderLeft: `1px solid ${COLORS.divider}`,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box id={LIST_SCROLL_ID} ref={listRef} sx={{ overflowY: "auto", flex: 1 }}>
					{items.length === 0 ? (
						<Box sx={{ p: 3, color: COLORS.muted, fontSize: 14 }}>
							No release notes yet.
						</Box>
					) : (
						items.map((update) => {
							const isSelected = update.id === selectedId;
							return (
								<Box
									key={update.id}
									role="button"
									tabIndex={0}
									aria-current={isSelected ? "true" : undefined}
									onClick={() => handleSelect(update)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											handleSelect(update);
										}
									}}
									sx={{
										display: "flex",
										gap: 1.25,
										px: 2.5,
										py: 1.75,
										borderBottom: `1px solid ${COLORS.divider}`,
										cursor: "pointer",
										borderLeft: isSelected
											? `3px solid ${COLORS.blue}`
											: "3px solid transparent",
										backgroundColor: isSelected
											? "rgba(48,122,215,0.08)"
											: "transparent",
										"&:hover": {
											backgroundColor: isSelected
												? "rgba(48,122,215,0.08)"
												: COLORS.hover,
										},
									}}
								>
									<Box
										component="button"
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											toggleRead(update);
										}}
										title={update.isRead ? "Mark as unread" : "Mark as read"}
										sx={{
											mt: 0.5,
											flexShrink: 0,
											p: "3px",
											border: "none",
											background: "none",
											cursor: "pointer",
											display: "inline-flex",
											"&:hover .rn-dot": {
												boxShadow: "0 0 0 4px rgba(48,122,215,0.15)",
												backgroundColor: COLORS.blue,
												borderColor: COLORS.blue,
											},
										}}
									>
										<Box
											className="rn-dot"
											sx={{
												width: 9,
												height: 9,
												borderRadius: "50%",
												backgroundColor: update.isRead
													? "transparent"
													: COLORS.blue,
												border: update.isRead
													? "1.5px solid #979797"
													: "none",
											}}
										/>
									</Box>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												gap: 1.25,
											}}
										>
											<Box
												sx={{
													font: update.isRead
														? "400 14px Roboto, sans-serif"
														: "700 14px Roboto, sans-serif",
													color: update.isRead ? COLORS.muted : COLORS.text,
												}}
											>
												{update.title}
											</Box>
											<Box
												sx={{
													fontSize: 12,
													color: COLORS.muted,
													flexShrink: 0,
												}}
											>
												{formatDate(update.publishedAt)}
											</Box>
										</Box>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												flexWrap: "wrap",
												gap: 0.75,
												mt: 0.5,
											}}
										>
											{update.version ? (
												<Box sx={{ fontSize: 12, color: COLORS.muted }}>
													{update.version}
												</Box>
											) : null}
											{audiencesFor(update).map((name) => (
												<Box key={name} sx={chipSx(name)}>
													{name}
												</Box>
											))}
										</Box>
										<Box
											sx={{
												fontSize: 13,
												color: COLORS.muted,
												mt: 0.5,
												overflow: "hidden",
												textOverflow: "ellipsis",
												display: "-webkit-box",
												WebkitLineClamp: 2,
												WebkitBoxOrient: "vertical",
												"& p": { m: 0, display: "inline" },
											}}
											dangerouslySetInnerHTML={{ __html: update.shortDescription }}
										/>
									</Box>
								</Box>
							);
						})
					)}
					{loadingMore ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
							<CircularProgress size={22} />
						</Box>
					) : null}
				</Box>
			</Box>

			<Box sx={{ flex: 1, p: 4.5, overflowY: "auto" }}>
				{selected ? (
					<>
						<Box
							sx={{
								font: "700 11px 'Roboto Condensed', sans-serif",
								color: COLORS.muted,
								letterSpacing: 0.8,
								textTransform: "uppercase",
								mb: 1,
							}}
						>
							Release notes
						</Box>
						<Box
							component="h1"
							sx={{ font: "700 26px Roboto, sans-serif", color: COLORS.text, m: 0, mb: 0.75 }}
						>
							{selected.title}
						</Box>
						<Box sx={{ fontSize: 13, color: COLORS.muted, mb: 2.5 }}>
							{["Released " + formatDate(selected.publishedAt), selected.version]
								.filter(Boolean)
								.join(" · ")}
						</Box>

						{selected.displayImageUrl ? (
							<Box
								component="img"
								src={selected.displayImageUrl}
								alt=""
								sx={{
									display: "block",
									width: "100%",
									// Show the whole image (4:3 screenshots, portrait, wide) without
									// cropping; letterbox against the neutral background instead.
									maxHeight: 480,
									objectFit: "contain",
									mb: 3,
									borderRadius: 1,
									backgroundColor: COLORS.heroBg,
								}}
							/>
						) : null}

						<Box
							sx={{
								fontSize: 14,
								lineHeight: 1.6,
								color: COLORS.text,
								mb: 3,
								"& p": { m: 0 },
								"& p + p": { mt: 1.5 },
							}}
							dangerouslySetInnerHTML={{ __html: selected.shortDescription }}
						/>

						{selected.externalUrl ? (
							<Button
								href={selected.externalUrl}
								target="_blank"
								rel="noopener noreferrer"
								endIcon={<LaunchRoundedIcon />}
								sx={{
									color: COLORS.blue,
									font: "700 13.5px 'Roboto Condensed', sans-serif",
									textTransform: "uppercase",
									letterSpacing: 0.3,
									p: 0,
								}}
							>
								Read full article on Freshdesk
							</Button>
						) : null}
					</>
				) : (
					<Box sx={{ color: COLORS.muted, fontSize: 14 }}>
						Select a release note to read it.
					</Box>
				)}
			</Box>
		</Box>
		</Box>
	);
}

export default ReleaseNotesPage;
